import type { ApiResponse } from 'apisauce';
import type { ErrorInfo } from '../shared/interfaces/error-info.interface';
import type { ResponseModel } from '../shared/interfaces/response-model.interface';
import api from '../shared/services/utils/api/api.service';
import errorHelper from '../shared/services/utils/api/error-helper.service';
import {
  CreateHousekeepingOrderPayload,
  HousekeepingOrderItem,
  HousekeepingOrderSearchQuery
} from './types';

const MODULE_NAME = 'HOUSE_KEEPING_MODULE';

const mapHousekeepingOrderItem = (item: any): HousekeepingOrderItem => ({
  id: String(item.id),
  unitName: item.unitName,
  projectName: item.projectName,
  unitTypeGroup: item.unitTypeGroupName || item.unitTypeGroup,
  unitShortName: item.unitShortName,
  orderDate: item.orderDate,
  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
  status: item.status,
  assigneeName: item?.assignedToUserName || '',
  images: Array.isArray(item.images) ? item.images : []
});

export function getHousekeepingOrders(
  query: HousekeepingOrderSearchQuery
): Promise<{ data: HousekeepingOrderItem[]; count: number } | ErrorInfo> {
  return api
    .searchHousekeepingOrders(query)
    .then((response: ApiResponse<ResponseModel<any[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          const normalized = (data.data || []).map(mapHousekeepingOrderItem);

          return {
            data: normalized,
            count: data.count || data.total || normalized.length
          };
        }

        return errorHelper(0, '', MODULE_NAME, (api.searchHousekeepingOrders as any).displayName);
      }

      return errorHelper(
        Number(response.status),
        response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
        MODULE_NAME,
        (api.searchHousekeepingOrders as any).displayName
      );
    });
}

export function updateHousekeepingOrderStatus(
  orderId: string,
  status: HousekeepingOrderItem['status']
): Promise<HousekeepingOrderItem | ErrorInfo> {
  return api
    .updateHousekeepingOrder(orderId, { status })
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return mapHousekeepingOrderItem(data.data);
        }

        return errorHelper(0, '', MODULE_NAME, (api.updateHousekeepingOrder as any).displayName);
      }

      return errorHelper(
        Number(response.status),
        response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
        MODULE_NAME,
        (api.updateHousekeepingOrder as any).displayName
      );
    });
}

export function assignHousekeepingOrderToSelf(
  orderId: string
): Promise<HousekeepingOrderItem | ErrorInfo> {
  return api
    .assignHousekeepingOrderToSelf(orderId)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return mapHousekeepingOrderItem(data.data);
        }

        return errorHelper(
          0,
          '',
          MODULE_NAME,
          (api.assignHousekeepingOrderToSelf as any).displayName
        );
      }

      return errorHelper(
        Number(response.status),
        response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
        MODULE_NAME,
        (api.assignHousekeepingOrderToSelf as any).displayName
      );
    });
}

export function assignHousekeepingOrderUsers(
  orderId: string,
  payload: { assignedToId?: string }
): Promise<HousekeepingOrderItem | ErrorInfo> {
  return api
    .updateHousekeepingOrder(orderId, payload as any)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return mapHousekeepingOrderItem(data.data);
        }

        return errorHelper(0, '', MODULE_NAME, (api.updateHousekeepingOrder as any).displayName);
      }

      return errorHelper(
        Number(response.status),
        response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
        MODULE_NAME,
        (api.updateHousekeepingOrder as any).displayName
      );
    });
}

export function uploadHousekeepingOrderImages(
  orderId: string,
  files: {
    bedroom1Photo: File;
    bedroom2Photo: File;
    bathroomPhoto: File;
    kitchenPhoto: File;
    livingAreaPhoto: File;
  }
): Promise<any | ErrorInfo> {
  const data = new FormData();
  data.append('bedroom1', files.bedroom1Photo);
  data.append('bedroom2', files.bedroom2Photo);
  data.append('bathroom', files.bathroomPhoto);
  data.append('kitchen', files.kitchenPhoto);
  data.append('livingArea', files.livingAreaPhoto);

  return api
    .uploadHousekeepingOrderImages(orderId, data)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data && data.data) {
          return data.data;
        }

        return {};
      }

      return errorHelper(
        Number(response.status),
        response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
        MODULE_NAME,
        (api.uploadHousekeepingOrderImages as any).displayName
      );
    });
}

export function assignHousekeepingUsers(payload: {
  unitTypeIds?: string[];
  projectId?: string;
  assignedToId?: string;
}): Promise<any | ErrorInfo> {
  return api
    .assignHousekeepingUsers(
      payload as { unitTypeIds?: string[]; projectId?: string; assignedToId: string }
    )
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return data.data;
        }

        return errorHelper(0, '', MODULE_NAME, (api.assignHousekeepingUsers as any).displayName);
      }

      return errorHelper(
        Number(response.status),
        response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
        MODULE_NAME,
        (api.assignHousekeepingUsers as any).displayName
      );
    });
}

export function createHousekeepingOrder(
  payload: CreateHousekeepingOrderPayload
): Promise<HousekeepingOrderItem | ErrorInfo> {
  return api
    .createHousekeepingOrder(payload)
    .then((response: ApiResponse<ResponseModel<any>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data?.data) {
          return mapHousekeepingOrderItem(data.data);
        }

        return errorHelper(0, '', MODULE_NAME, (api.createHousekeepingOrder as any).displayName);
      }

      return errorHelper(
        Number(response.status),
        response.data?.errorCode ? String(response.data.errorCode || '') : response.problem,
        MODULE_NAME,
        (api.createHousekeepingOrder as any).displayName
      );
    });
}
